import React from 'react';
import { Helmet } from 'react-helmet-async';
import HighlightText from '../components/HighlightText';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy | MyHomeTutors Lucknow',
  description: 'Read the privacy policy of MyHomeTutors Lucknow. We are committed to protecting your personal information.',
  url: 'https://myhometutors.org/privacy-policy',
  publisher: {
    '@type': 'Organization',
    name: 'MyHomeTutors',
    telephone: '+919415905584',
  },
};

const sections = [
  {
    title: '1. Information We Collect',
    content: [
      'When you use MyHomeTutors, we may collect the following types of information:',
      '• Personal Identification: Name, phone number, email address, and location (area in Lucknow).',
      '• Academic Information: Class, subject, and tutoring requirements shared during inquiry or registration.',
      '• Tutor Information: Qualifications, experience, subject expertise, and background verification documents provided by tutors during registration.',
      '• Usage Data: Pages visited, time spent on the site, device type, and browser information collected automatically.',
    ],
  },
  {
    title: '2. How We Use Your Information',
    content: [
      'We use the information we collect for the following purposes:',
      '• To match students with suitable verified home tutors in their area.',
      '• To contact you with tutor recommendations, service updates, and booking confirmations.',
      '• To process tutor registrations and conduct background verification.',
      '• To improve our platform, services, and user experience.',
      '• To send relevant educational content, offers, or updates (you may opt out at any time).',
    ],
  },
  {
    title: '3. Information Sharing',
    content: [
      'MyHomeTutors does not sell, rent, or trade your personal information to third parties. We may share information only in the following circumstances:',
      '• With Tutors: Student contact details are shared with matched tutors solely for the purpose of arranging tuition sessions.',
      '• With Students/Parents: Basic tutor profile information (name, qualifications, experience) is shared to help parents make informed decisions.',
      '• Legal Requirements: We may disclose information if required by law or to protect the rights and safety of our users.',
    ],
  },
  {
    title: '4. Data Security',
    content: [
      'We take the security of your personal data seriously. We implement appropriate technical and organizational measures to protect your information from unauthorized access, alteration, disclosure, or destruction.',
      'However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.',
    ],
  },
  {
    title: '5. Cookies',
    content: [
      'Our website may use cookies to enhance your browsing experience. Cookies are small files stored on your device that help us remember your preferences and understand how you interact with our site.',
      'You can choose to disable cookies through your browser settings. However, some features of the website may not function properly if cookies are disabled.',
    ],
  },
  {
    title: '6. Third-Party Links',
    content: [
      'Our website may contain links to third-party websites (such as Google Maps, WhatsApp, Taskade). We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.',
    ],
  },
  {
    title: '7. Children\'s Privacy',
    content: [
      'Our services are designed to benefit students of all ages. However, personal information from children under 13 must be provided by a parent or guardian. We do not knowingly collect personal information from children without parental consent.',
    ],
  },
  {
    title: '8. Your Rights',
    content: [
      'You have the right to:',
      '• Access the personal information we hold about you.',
      '• Request correction of inaccurate or incomplete information.',
      '• Request deletion of your personal data (subject to legal obligations).',
      '• Opt out of marketing communications at any time.',
      'To exercise any of these rights, please contact us at +91 9415905584.',
    ],
  },
  {
    title: '9. Changes to This Policy',
    content: [
      'MyHomeTutors reserves the right to update this Privacy Policy at any time. Changes will be posted on this page with the updated date. Continued use of our services after any changes constitutes your acceptance of the revised policy.',
    ],
  },
  {
    title: '10. Contact Us',
    content: [
      'If you have any questions or concerns about this Privacy Policy, please contact us:',
      '📍 H.N 1/358, Vastu Khand, Gomti Nagar, Lucknow, Uttar Pradesh 226010',
      '📞 +91 9415905584',
    ],
  },
];

const PrivacyPolicyPage = () => (
  <div className="min-h-screen bg-[#fdf8f4]">
    <Helmet>
      <title>Privacy Policy | MyHomeTutors Lucknow</title>
      <meta name="description" content="Read the privacy policy of MyHomeTutors Lucknow. We are committed to protecting your personal data and information." />
      <link rel="canonical" href="https://myhometutors.org/privacy-policy/" />
      <meta property="og:title" content="Privacy Policy | MyHomeTutors Lucknow" />
      <meta property="og:description" content="Read the privacy policy of MyHomeTutors Lucknow." />
      <meta property="og:url" content="https://myhometutors.org/privacy-policy" />
      <meta property="og:type" content="website" />
    </Helmet>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

    {/* Header */}
    <section className="bg-gradient-to-br from-[#fff5f5] to-[#ffeaea] py-14 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
          Privacy <HighlightText>Policy</HighlightText>
        </h1>
        <p className="text-gray-600 font-medium text-lg">Last updated: January 2025</p>
        <p className="text-gray-500 font-medium mt-3 max-w-2xl mx-auto">
          MyHomeTutors is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information.
        </p>
      </div>
    </section>

    {/* Content */}
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-8 md:p-12 space-y-10">
          {sections.map((sec, i) => (
            <div key={i} className="pb-10 border-b border-gray-100 last:border-0 last:pb-0">
              <h2 className="text-xl font-extrabold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center text-primary text-xs font-extrabold flex-shrink-0">
                  {i + 1}
                </span>
                {sec.title.replace(/^\d+\.\s/, '')}
              </h2>
              <div className="space-y-2.5">
                {sec.content.map((line, j) => (
                  <p key={j} className={`font-medium leading-relaxed ${line.startsWith('•') ? 'text-gray-600 pl-4' : 'text-gray-600'}`}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
        <p className="text-sm font-bold text-slate-700">Questions about our privacy practices?</p>
        <p className="text-sm text-gray-500 font-medium mt-1">Contact us at <a href="tel:+919415905584" className="text-primary font-bold hover:underline">+91 9415905584</a></p>
      </div>
    </section>
  </div>
);

export default PrivacyPolicyPage;